'use client';

import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router-dom';
import { useAuthStore, type User as AuthUser } from '@/lib/stores/auth';
import { api } from '@/lib/api';
import { formatCurrency } from '@/lib/formatCurrency';
import { Avatar } from '@/components/ui/Avatar';
import { Button } from '@/components/ui/Button';
import { FollowButton } from '@/components/FollowButton';
import type { ProfileUser } from '@/pages/UserProfileLayout';
import { CrAmount } from '@/components/CrAmount';

type ProfileCardUser = ProfileUser | AuthUser;

export interface SpaceProfileCardProps {
  /** Target user to render. Defaults to the logged-in user (dashboard usage). */
  user?: ProfileCardUser;
  /** Whether the card is for the current user. Defaults to `true` (dashboard usage). */
  isSelf?: boolean;
  /** 关注/取关后回调（访客视图：重拉 target profile 刷新粉丝数）。 */
  onProfileChange?: () => void;
}

/**
 * 顶部空间资料卡：头像 + 名称 + 关注/粉丝 + 简介 + 操作区。
 * - 默认（无 props，dashboard 用法）：渲染当前登录用户，操作区显示
 *   「修改用户信息」按钮 + 钱包余额（自取 /api/billing/wallet）。
 * - 传入 `{ user, isSelf: false }`（用户主页访客视图）：渲染该用户，
 *   操作区显示 FollowButton，不取/不显示钱包。关注/粉丝数字始终显示。
 */
export function SpaceProfileCard({ user: userProp, isSelf: isSelfProp, onProfileChange }: SpaceProfileCardProps = {}) {
  const { t } = useTranslation(['dashboard', 'common']);
  const { user: me } = useAuthStore();
  const { locale } = useParams<{ locale: string }>();
  const [balance, setBalance] = useState<string | null>(null);

  const user = userProp ?? me;
  const isSelf = isSelfProp ?? true;

  useEffect(() => {
    if (!isSelf) return; // 仅自视图拉取钱包
    let cancelled = false;
    api
      .get<{ balance: string }>('/api/billing/wallet')
      .then((r) => {
        if (!cancelled) setBalance(r.balance);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [isSelf]);

  if (!user) return null;
  // locale 来自路由前缀；兜底 zh-CN（与 DashboardPage 一致）
  const localePrefix = locale || 'zh-CN';

  return (
    <div className="mb-6 glass-card rounded-xl overflow-hidden">
      {/* 预留：日后 user 可上传卡片背景图（TODO）—— 此处 bg 层 */}
      <div className="p-5 flex items-start justify-between gap-4">
        <div className="flex items-start gap-4 min-w-0">
          <Avatar src={user.avatarUrl ?? undefined} alt={user.name} fallback={user.name} size="xl" />
          <div className="min-w-0">
            <h1 className="text-xl sm:text-2xl font-bold text-foreground truncate">
              {user.name}
            </h1>
            <p className="mt-1 text-sm text-muted-foreground">
              {t('userCard.following', { count: user.following ?? 0, ns: 'common' })} ·{' '}
              {t('userCard.followers', { count: user.followers ?? 0, ns: 'common' })}
            </p>
            {user.bio && (
              <p className="mt-2 text-sm text-foreground/80 line-clamp-2">{user.bio}</p>
            )}
          </div>
        </div>

        <div className="flex flex-col items-end gap-3 shrink-0">
          {isSelf ? (
            <>
              {balance !== null && (
                <div className="text-right">
                  <p className="text-xs text-muted-foreground">{t('walletBalance')}</p>
                  <p className="text-xl font-bold text-brand-600 font-mono leading-tight">
                    <CrAmount usd={balance} />
                  </p>
                </div>
              )}
              <Button asChild variant="outline" size="sm">
                <Link to={`/${localePrefix}/dashboard/settings`}>
                  {t('profileCard.edit')}
                </Link>
              </Button>
            </>
          ) : (
            <FollowButton userId={user.id} onFollowChange={onProfileChange} />
          )}
        </div>
      </div>
    </div>
  );
}
